import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { researchAPI } from '../services/api';
import { statusLabel, priorityTone, hasActive } from '../utils/research';
import ResearchRunDialog from './ResearchRunDialog';
import SetupErrorMessage from './SetupErrorMessage';
import { parseServerDate } from '../utils/serverDate';
import './ResearchPanel.css';

const MAX_POLL_MS = 5 * 60 * 1000;
const POLL_MS = 5000;
const MET_LABEL = { YES: 'Yes', NO: 'No', UNKNOWN: 'Unknown' };

const researchedLine = (result) => {
  const d = parseServerDate(result.completed_at);
  const parts = [];
  if (d) parts.push(`Researched ${d.toLocaleDateString()}`);
  if (result.model) parts.push(result.model);
  return parts.join(' · ');
};

const ResultBlock = ({ result, onRetry }) => {
  const [open, setOpen] = useState(false);
  const out = result.output;
  const criteria = out && Array.isArray(out.criteria) ? out.criteria : [];
  const active = result.status === 'pending' || result.status === 'running';
  return (
    <div className="research-result">
      <div className="research-result-head">
        <strong>{result.profile_name || 'Research'}</strong>
        <span className="research-status" aria-live="polite">{active ? 'Research pending' : statusLabel(result)}</span>
      </div>
      {result.status === 'complete' && out && (
        <>
          <div className={`research-score research-score--${priorityTone(out.priority)}`}>
            AI Fit: {out.score}/100{out.priority ? ` — ${out.priority}` : ''}
          </div>
          {out.business_type && <div className="research-line">{out.business_type}</div>}
          {out.reasoning && <p><strong>Why:</strong> {out.reasoning}</p>}
          {out.potential_problem && <p><strong>Potential problem:</strong> {out.potential_problem}</p>}
          {out.personalisation_opportunity && (
            <p><strong>Personalisation (suggestion):</strong> {out.personalisation_opportunity}</p>
          )}
          {out.recommended_action && <p><strong>Action:</strong> {out.recommended_action}</p>}
          {researchedLine(result) && <p className="research-empty">{researchedLine(result)}</p>}
          <button
            type="button"
            className="research-link"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
          >
            {open ? 'Hide details' : 'Show full research'}
          </button>
          {open && (
            <>
              {criteria.length === 0 && <p className="research-empty">No criteria details.</p>}
              <ul className="research-criteria">
                {criteria.map((c, i) => {
                  const met = String((c && c.met) || 'UNKNOWN').toUpperCase();
                  const metKey = MET_LABEL[met] ? met : 'UNKNOWN';
                  return (
                    <li key={`${i}-${(c && c.criterion) || ''}`}>
                      <span className={`research-met research-met--${metKey.toLowerCase()}`}>{MET_LABEL[metKey]}</span>
                      {c && c.criterion}
                      {c && c.evidence && <div className="research-evidence">“{c.evidence}”</div>}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </>
      )}
      {result.status === 'failed' && (
        <>
          <p className="research-error">
            <SetupErrorMessage message={result.error_message} fallback="Research failed." />
          </p>
          <button type="button" className="research-link" onClick={() => onRetry(result)}>Try again</button>
        </>
      )}
    </div>
  );
};

// Profile of the most recently updated result (null if none or its profile was deleted).
const latestProfileId = (results) => {
  if (!Array.isArray(results) || !results.length) return null;
  const latest = results.reduce((a, b) => (String(b.updated_at || '') > String(a.updated_at || '') ? b : a));
  return latest.profile_id == null ? null : latest.profile_id;
};

const ResearchPanel = ({ buyerId, onDialogOpenChange }) => {
  const [results, setResults] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [pollEpoch, setPollEpoch] = useState(0);
  const [retryProfileId, setRetryProfileId] = useState(null);
  const startedAt = useRef(Date.now());
  const mounted = useRef(true);
  const currentBuyer = useRef(buyerId);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Tell the slide-over a dialog is open so Esc/backdrop do not close it underneath.
  useEffect(() => {
    if (onDialogOpenChange) onDialogOpenChange(dialogOpen);
  }, [dialogOpen, onDialogOpenChange]);
  useEffect(() => () => { if (onDialogOpenChange) onDialogOpenChange(false); }, [onDialogOpenChange]);

  const load = useCallback(async () => {
    const forId = buyerId;
    try {
      const res = await researchAPI.forBuyer(forId);
      if (!mounted.current || currentBuyer.current !== forId) return;
      setResults((res && res.data && res.data.results) || []);
      setLoadError(false);
    } catch {
      if (!mounted.current || currentBuyer.current !== forId) return;
      setLoadError(true);
      setResults((prev) => prev || []);
    }
  }, [buyerId]);

  useEffect(() => {
    currentBuyer.current = buyerId;
    startedAt.current = Date.now();
    setResults(null);
    setLoadError(false);
    load();
  }, [buyerId, load]);

  const active = hasActive(results);
  useEffect(() => {
    if (!active) return undefined;
    startedAt.current = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt.current > MAX_POLL_MS) { clearInterval(timer); return; }
      load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [active, load, pollEpoch]);

  const onQueued = () => { setPollEpoch((n) => n + 1); load(); };

  const openDialog = (profileId) => {
    setRetryProfileId(profileId == null ? null : profileId);
    setDialogOpen(true);
  };

  return (
    <section className="research-panel pd-sec" aria-label="AI Research">
      <div className="research-panel-head">
        <h4 className="research-title"><Sparkles size={14} aria-hidden="true" /> AI Research</h4>
        <button type="button" className="btn btn-secondary" onClick={() => openDialog(latestProfileId(results))}>
          {results && results.length ? 'Research again' : 'Research'}
        </button>
      </div>
      {results === null && <p className="research-empty">Loading…</p>}
      {loadError && <p className="research-error">Could not load research. Try again shortly.</p>}
      {results && results.length === 0 && !loadError && (
        <p className="research-empty">Not researched yet. Score this prospect against one of your profiles.</p>
      )}
      {(results || []).map((r) => (
        <ResultBlock key={r.id} result={r} onRetry={(res) => openDialog(res.profile_id)} />
      ))}
      <ResearchRunDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        buyerIds={[buyerId]}
        onQueued={onQueued}
        initialProfileId={retryProfileId}
      />
    </section>
  );
};

export default ResearchPanel;
